import React from 'react';
import { Callout, DocHeading } from './components';

function ErrorList({ items }) {
  return (
    <div className="space-y-3">
      {items.map(e => (
        <details key={e.code} className="card group">
          <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none select-none">
            <div className="flex items-center gap-3">
              <span className="text-red-400 flex-shrink-0">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                  <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" />
                </svg>
              </span>
              <span className="font-medium text-white text-sm">{e.code}</span>
            </div>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-gray-500 flex-shrink-0 transition-transform group-open:rotate-180">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </summary>
          <div className="px-5 pb-4 pt-1 text-sm text-gray-400 border-t border-white/[0.06]">
            {e.fix}
          </div>
        </details>
      ))}
    </div>
  );
}

function PageCommonErrors({ navigate }) {
  const loaderErrors = [
    {
      code: 'Loader crashes / won\'t open',
      fix: 'Make sure the folder is excluded from Windows Defender and antivirus before running. Right-click the exe → Properties → check Unblock if present. Run as Administrator. DirectX 11 is required (built into all Windows 10+ machines — no install needed).',
    },
    {
      code: '[ERROR] Game not running!',
      fix: 'Launch Drakensang Online and log in to a character before pressing Inject. The loader must detect a running dro_client64.exe process.',
    },
    {
      code: '[ERROR] Invalid subscription!',
      fix: 'Your subscription is expired, frozen, or the key has not been activated yet. Check the Subscription section in your Dashboard.',
    },
    {
      code: '[ERROR] Load failed!',
      fix: 'The server could not deliver the payload. Check your internet connection. If it persists, the servers may be under maintenance — check Discord for announcements.',
    },
    {
      code: '[ERROR] Failed!',
      fix: 'Injection failed. Run the loader as Administrator. Temporarily disable Windows Defender and antivirus, then retry. Make sure the game is fully loaded and you are in a zone.',
    },
    {
      code: 'Bot stops when loader is closed',
      fix: 'This is by design. The bot is kept alive by the loader process — closing the loader window will terminate the bot immediately. Keep the loader open while farming.',
    },
    {
      code: 'Loader version outdated',
      fix: 'Download the latest version using the Download Loader button in your Dashboard. Old versions stop working after a server-side update.',
    },
    {
      code: 'HWID mismatch error',
      fix: 'Your license is bound to a different machine. Open a Discord ticket to request an HWID reset. See the HWID Issues page for the full process.',
    },
  ];

  const inGame = [
    {
      code: 'The menu does not open with INSERT',
      fix: 'Click the game window once so it has focus, then press INSERT again. If it still does nothing, the injection did not complete — check the loader for [OK] Success!.',
    },
    {
      code: 'Game tick not firing — move your character',
      fix: 'The bot rides the game’s own heartbeat, and it can stall while you stand perfectly still in a game menu. Take one step. If it persists, close the game’s open windows (inventory, map, shop).',
    },
    {
      code: 'START is greyed out on AUTOPILOT › Play',
      fix: 'Click it anyway and the reason appears in orange. It is one of three: "Fix the problems first" (the Editor tab marks what is missing), "Go to a map that is part of this route", or "Stop recording first".',
    },
    {
      code: 'Waiting — this map is not part of the route',
      fix: 'You ended up somewhere the route does not know (a town, a wrong portal, an event map). Walk back to any map of the route and it picks itself up on its own.',
    },
    {
      code: 'The bot runs past the monsters I marked',
      fix: 'Marked kill targets are ignored until "Also the route’s kill targets" is switched on at AUTOPILOT › Combat. That switch is off by default.',
    },
    {
      code: 'Every skill row says no target',
      fix: 'The monsters do not match your Fight setting, or they are further away than "Fight within". Both are on AUTOPILOT › Combat.',
    },
    {
      code: 'Skills say out of range and nothing happens',
      fix: '"Fight within" lets the bot pick a target, but each skill still needs to reach it. If "Fight within" is wider than your skills reach, a target is chosen that cannot be hit yet. Lower it, or use a longer-ranged skill.',
    },
    {
      code: 'It picks up nothing, though items are lying there',
      fix: 'Check FARM › Loot: "In range" counts the items, "Matching filter" counts what your filter allows. If the second is 0, the filter is excluding them. If "Needs a free slot" is climbing, the bag is full.',
    },
    {
      code: 'It does not gather anything',
      fix: 'On FARM › Gather, check the WHAT TO GATHER card: Plants and Ores are separate switches, and chest types have to be ticked one by one in the Chests dropdown. If a notification said "Out of Multitools", plants and ores are skipped until you buy more.',
    },
    {
      code: 'A feature stopped: "does not look like it used to" or "unexpected command layout"',
      fix: 'A game patch moved something, and that feature locked itself down on purpose rather than send something wrong. Nothing you can fix locally — wait for the update, and watch Discord.',
    },
    {
      code: 'The game crashed while the bot was running',
      fix: 'Open SETTINGS › CRASH REPORTS and note the last report file name, then open the data folder with OPEN FOLDER. Send us that file plus the version number from the ABOUT card.',
    },
  ];

  return (
    <div>
      <p className="text-sm text-brand-400 font-medium mb-2">Troubleshooting</p>
      <h1 className="font-display text-3xl font-bold text-white tracking-tight mb-3">Common Errors</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        The most frequently reported issues and what to do about them &mdash; first the loader,
        then the bot itself once it is in the game.
      </p>

      <DocHeading>Loader &amp; Injection</DocHeading>
      <ErrorList items={loaderErrors} />

      <DocHeading>In the Game</DocHeading>
      <ErrorList items={inGame} />

      <Callout type="info">
        If your error is not listed here, open a ticket on Discord. Include your loader version, the
        version number from the <strong>SETTINGS &rsaquo; ABOUT</strong> card, and the exact message
        you see.
      </Callout>
    </div>
  );
}

export default PageCommonErrors;
